import React from 'react';
import Modal from '@gik/ui/Modal';
import Button from '@gik/ui/Button';
import OpenMoji, { OpenMojiKey } from '@gik/ui/OpenMoji';
import RangeSlider from '@gik/ui/RangeSlider';
import ModalButtonFooter from '@gik/ui/Modal/ModalButtonFooter';
import { gikClassPrefix } from '@gik/core/constants';

export interface ITipModalProps {
  isOpen: boolean;
  totalCheckoutPrice: number;
  onClose?: () => void;
  onSave?: (percentage: number, amount: number) => void;
  onChange?: (percentage: number, amount: number) => void;
  onInit?: (percentage: number, amount: number) => void;
  percentageValue?: number;
  defaultValue?: number;
  className?: string;
}

const blockName = `${gikClassPrefix}-tip-modal`;

export default function TipModal({
  isOpen,
  totalCheckoutPrice,
  onClose,
  onSave,
  onChange,
  onInit,
  percentageValue,
  defaultValue = 10,
  className,
}: ITipModalProps): React.ReactElement {
  const [sliderValue, setSliderValue] = React.useState<number>(defaultValue);
  const [ready, setReady] = React.useState<boolean>(false);

  if (!Number.isInteger(defaultValue) || defaultValue > 40 || defaultValue < 0) {
    throw new Error(`defaultValue prop ${defaultValue} must be a whole number in range 0-40.`);
  }

  if (
    percentageValue !== undefined &&
    (!Number.isInteger(percentageValue) || percentageValue > 40 || percentageValue < 0)
  ) {
    throw new Error(`percentageValue prop ${percentageValue} must be a whole number in range 0-40 or undefined.`);
  }

  const _onClickSave = (percentage: number): void => {
    if (onSave) onSave(percentage, getAmount(percentage));
  };

  const _onClickCancel = (): void => {
    if (onClose) onClose();
  };

  const onSliderChange = (percentage: number): void => {
    setSliderValue(percentage);
    if (onChange) onChange(percentage, getAmount(percentage));
  };

  const getAmount = (percentage: number): number => {
    return Math.ceil(totalCheckoutPrice * (percentage / 100)); // round up
  };

  const getAmountCallback = React.useCallback(getAmount, [totalCheckoutPrice]);

  const getEmojiNameFromPercentage = (percentage: number): OpenMojiKey => {
    switch (true) {
      case percentage < 1:
        return 'pleading-face'; // 0
      case percentage < 4:
        return 'slightly-smiling-face'; // 1 - 3
      case percentage < 7:
        return 'smiling-face-with-smiling-eyes'; // 4 - 6
      case percentage < 10:
        return 'grinning-face'; // 7 - 9
      case percentage < 11:
        return 'grinning-face-with-big-eyes'; // 10
      case percentage < 16:
        return 'grinning-face-with-smiling-eyes'; // 11 - 15
      case percentage < 25:
        return 'beaming-face-with-smiling-eyes'; // 16 - 24
      case percentage < 31:
        return 'hugging-face'; // 25 - 30
      case percentage < 40:
        return 'smiling-face-with-heart-eyes'; // 31 - 39
      case percentage === 40:
        return 'smiling-face-with-hearts'; // 40
      default:
        throw new Error(`No OpenMoji key for ${percentage} percentage.`);
    }
  };

  React.useEffect(() => {
    const percentage = percentageValue !== undefined ? percentageValue : defaultValue;
    if (!isOpen) {
      setTimeout(setSliderValue, 500, percentage);
    }
  }, []);

  React.useEffect(() => {
    const percentage = percentageValue !== undefined ? percentageValue : defaultValue;
    if (!ready) {
      setReady(true);
      if (onInit) onInit(percentage, getAmountCallback(percentage));
    }
  }, [ready, defaultValue, percentageValue, onInit, getAmountCallback]);

  const getEmoji = (percentage: number): React.ReactElement => {
    const emojiName = getEmojiNameFromPercentage(percentage);
    return <OpenMoji name={emojiName} />;
  };

  const cancelButton = (
    <Button key="cancel" onClick={_onClickCancel} variant="default">
      Cancel
    </Button>
  );

  const saveButton = (
    <Button key="save" onClick={() => _onClickSave(sliderValue)} variant="primary">
      Save
    </Button>
  );

  const content = (
    <>
      <p className={`${blockName}__title`}>Keep Give InKind Free</p>
      <div className={`${blockName}__content`}>
        <div>
          <p className={`${blockName}__description`}>
            We don&#39;t charge any fees to organize support through Give InKind, and your optional contribution makes
            that possible.
          </p>
        </div>
        <div className={`${blockName}__values`}>
          <span className={`${blockName}__amount`}>${`${getAmount(sliderValue)}`}</span>
          <span className={`${blockName}__percentage`}>&nbsp;{`(${sliderValue}%)`}</span>
        </div>
        <div className={`${blockName}__icon-container`}>{getEmoji(sliderValue)}</div>
        <div className={`${blockName}__slider-container`}>
          <div className={`${blockName}__slider-labels`}>
            <span>0%</span>
            <span>40%</span>
          </div>
          <RangeSlider
            min={0}
            max={40}
            value={sliderValue}
            onChange={onSliderChange}
            step={1}
            defaultValue={defaultValue}
          />
        </div>
      </div>
    </>
  );

  return (
    <Modal
      className={`${blockName} dialog ${className ? className : ''}`}
      isOpen={isOpen}
      pages={[
        {
          title: '',
          content: <>{content}</>,
        },
      ]}
      pageNumber={1}
      onClose={onClose}
      header={<div />}
      footer={<ModalButtonFooter buttons={[cancelButton, saveButton]} />}
    />
  );
}
